import React from 'react';
import { Select as AntSelect, Tooltip } from 'antd';
import type { SelectProps as AntSelectProps } from 'antd';
import type { BaseOptionType, DefaultOptionType } from 'antd/es/select';

import { useAuthDecision } from '../../auth';
import type { AuthProps } from '../../auth';
import { useUIText } from '../../i18n';

// EAA-1666: ValueType defaults to `any` (not `unknown`) to exactly match
// antd's own `SelectProps` default — callers relying on implicit inference
// (e.g. `onChange={(v) => updateFilter('status', v)}`) must keep typechecking
// unchanged; `unknown` broke several such call sites across web-content.
export type SelectProps<
    ValueType = any,
    OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> = AntSelectProps<ValueType, OptionType> & AuthProps;

/**
 * Themed, auth-gated pass-through of antd's `Select`. Hand-written (not
 * built on `withAuthGuard`) to keep antd's own two type parameters
 * (`ValueType`, `OptionType`) intact — `withAuthGuard`'s `forwardRef`-based
 * signature collapses a wrapped component's generics to one concrete
 * instantiation, which broke call-site inference for `options`/`onChange`
 * (see `FieldSelect.tsx` for the same precedent in `web-content`).
 */
function SelectInner<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ValueType = any,
    OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
>(props: SelectProps<ValueType, OptionType>): React.ReactElement {
    const { auth, authFallback = 'disable', ...rest } = props;
    const decision = useAuthDecision(auth, authFallback);
    const { common } = useUIText();
    const selectProps = rest as AntSelectProps<ValueType, OptionType>;

    if (decision === 'allow') {
        return <AntSelect {...selectProps} />;
    }

    if (decision === 'hide') {
        return <></>;
    }

    if (decision === 'tooltip') {
        return (
            <Tooltip title={common.noPermissionTooltip}>
                <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                    <AntSelect {...selectProps} disabled />
                </span>
            </Tooltip>
        );
    }

    return <AntSelect {...selectProps} disabled />;
}

export const Select = SelectInner as typeof SelectInner & {
    Option: typeof AntSelect.Option;
    OptGroup: typeof AntSelect.OptGroup;
};
Select.Option = AntSelect.Option;
Select.OptGroup = AntSelect.OptGroup;
